import type { Dataset, DatasetSortBy } from '@dmp/api/api.types';
import { produce } from 'immer';
import { create } from 'zustand';

import type { FilterItem, OptionMap } from './DataCatalog.type';

type DatasetsState = {
  datasets: Dataset[];
  search: string;
  sortBy: DatasetSortBy;
  page: number;
  pageSize: number;
  total: number;
  liked: boolean;
  filterOptionsMap: Record<string, OptionMap>;
  filters: FilterItem[];
  filterValues: Record<string, string[]>;
  searchableKeys: string[];
  isFetchingDatasets: boolean;
  isFetchingFilterOptions: boolean;
  searchContainerDom: null | HTMLDivElement;
  isGuidedSearch: boolean;
  guidedSearchWords: string[];
};

export const initialDatasetsState: DatasetsState = {
  datasets: [],
  search: '',
  sortBy: 'recent',
  page: 1,
  pageSize: 15,
  total: 0,
  liked: false,
  filterOptionsMap: {},
  filters: [],
  filterValues: {},
  searchableKeys: [],
  isFetchingDatasets: false,
  isFetchingFilterOptions: false,
  searchContainerDom: null,
  isGuidedSearch: false,
  guidedSearchWords: [],
};

export const useDatasetsStore = create<DatasetsState>()(() => initialDatasetsState);

export const setDatasets = (datasets: Dataset[]) => useDatasetsStore.setState({ datasets });

export const setSearch = (search: string) => useDatasetsStore.setState({ search });

export const setSortBy = (sortBy: DatasetSortBy) => useDatasetsStore.setState({ sortBy });

export const setPageSize = (pageSize: number) => useDatasetsStore.setState({ pageSize });

export const setPage = (page: number) => useDatasetsStore.setState({ page });

export const setTotal = (total: number) => useDatasetsStore.setState({ total });

export const setLiked = (liked: boolean) => useDatasetsStore.setState({ liked });

export const setFilterOptionsMap = (filterOptionsMap: DatasetsState['filterOptionsMap']) =>
  useDatasetsStore.setState({ filterOptionsMap });

export const setFilters = (filters: FilterItem[]) => useDatasetsStore.setState({ filters });

export const setFilterValues = (filterValues: Record<string, string[]>) => useDatasetsStore.setState({ filterValues });

export const setIsFetchingDatasets = (isFetchingDatasets: boolean) => useDatasetsStore.setState({ isFetchingDatasets });

export const setIsFetchingFilterOptions = (isFetchingFilterOptions: boolean) =>
  useDatasetsStore.setState({ isFetchingFilterOptions });

export const setSearchContainerDom = (searchContainerDom: null | HTMLDivElement) =>
  useDatasetsStore.setState({ searchContainerDom });

export const setSearchableKeys = (searchableKeys: string[]) => useDatasetsStore.setState({ searchableKeys });

export const setIsGuidedSearch = (isGuidedSearch: boolean) => useDatasetsStore.setState({ isGuidedSearch });

export const resetDatasetsState = (options?: { keepFilterOptions?: boolean }) => {
  useDatasetsStore.setState(
    produce(initialDatasetsState, (draft) => {
      if (options?.keepFilterOptions) {
        draft.filterOptionsMap = useDatasetsStore.getState().filterOptionsMap;
        draft.filters = useDatasetsStore.getState().filters;
      }
    })
  );
};
